import { Fraction } from './fraction'

const coreDebugEnabled =
  typeof globalThis !== 'undefined' &&
  Boolean((globalThis as { __SCULPTOR_DEBUG_CORE__?: boolean }).__SCULPTOR_DEBUG_CORE__)

const coreLog = (...args: unknown[]): void => {
  if (coreDebugEnabled) {
    console.log(...args)
  }
}

/**
 * Cutting Plane Generation Functions (CGF)
 * Implements various cutting plane generation algorithms with exact arithmetic
 */

/**
 * Compute fractional part [x] = x - floor(x) for Fraction
 */
function fractionalPart(x: Fraction): Fraction {
  return x.fractionalPart()
}

/**
 * Gomory Mixed-Integer (GMI) cut generating function
 * π_GMI(r) = [r] / [f]           if [r] <= [f]
 *          = (1 - [r]) / (1 - [f]) if [r] > [f]
 * 
 * This is a single-row CGF and only accepts single-row input.
 * 
 * @param f - RHS fractional value
 * @param rays - Tableau rows (without RHS) - must be single row for GMI
 * @returns Cut coefficients in standard form
 */
export function pi_gmi(f: Fraction, rays: Fraction[][]): Fraction[] {
  // Reject multi-row input - GMI is a single-row CGF
  if (rays.length > 1) {
    throw new Error('pi_gmi only supports single-row cuts')
  }
  
  if (rays.length === 0) {
    throw new Error('No rays provided')
  }
  
  // Exact fraction computation
  const f_part = fractionalPart(f)
  
  if (f_part.isZero() || f_part.equals(Fraction.one())) {
    throw new Error('f must have fractional part not equal to 0 or 1')
  }
  
  // Generate coefficients for each column
  const coefficients: Fraction[] = []
  const ray = rays[0]
  
  for (const r of ray) {
    const r_part = fractionalPart(r)
    
    let coeff: Fraction
    if (r_part.lessThanOrEqual(f_part)) {
      coeff = r_part.divide(f_part)
    } else {
      coeff = Fraction.one().subtract(r_part).divide(Fraction.one().subtract(f_part))
    }
    
    coefficients.push(coeff)
  }
  
  return coefficients
}

/**
 * Gomory fractional cut generating function
 * π_Gomory(r) = [r] / [f]
 * 
 * This is a single-row CGF and only accepts single-row input.
 * 
 * @param f - RHS fractional value
 * @param rays - Tableau rows (without RHS) - must be single row for Gomory
 * @returns Cut coefficients in standard form
 */
export function pi_gomory(f: Fraction, rays: Fraction[][]): Fraction[] {
  // Reject multi-row input - Gomory is a single-row CGF
  if (rays.length > 1) {
    throw new Error('pi_gomory only supports single-row cuts')
  }
  
  if (rays.length === 0) {
    throw new Error('No rays provided')
  }
  
  // Exact fraction computation
  const f_part = fractionalPart(f)
  
  if (f_part.isZero()) {
    throw new Error('f must have non-zero fractional part')
  }
  
  // Generate coefficients for each column
  const coefficients: Fraction[] = []
  const ray = rays[0]
  
  for (const r of ray) {
    const r_part = fractionalPart(r)
    const coeff = r_part.divide(f_part)
    coefficients.push(coeff)
  }
  
  return coefficients
}

/**
 * 2D-Simplex cut generating function for 2-row cuts
 * Uses exact fraction arithmetic with optimization over parameter μ
 * 
 * This is a 2-row CGF and requires exactly 2 rows.
 * 
 * @param f - RHS fractional values [f1, f2] for the two rows
 * @param rays - Tableau rows (without RHS) - must be exactly 2 rows for 2D-Simplex
 * @param showBestMu - If true, return {coefficients, optimalMu}. If false, return coefficients only.
 * @returns Cut coefficients in standard form (and optionally optimal μ)
 */
export function pi_2dsimplex(
  f: Fraction[], 
  rays: Fraction[][], 
  showBestMu: boolean = false
): Fraction[] | { coefficients: Fraction[], optimalMu: Fraction } {
  if (rays.length !== 2) {
    throw new Error('pi_2dsimplex requires exactly 2 rows')
  }
  if (f.length !== 2) {
    throw new Error('f must have exactly 2 elements for 2D-Simplex')
  }
  const [f1, f2] = f
  const [ray1, ray2] = rays
  if (ray1.length !== ray2.length) {
    throw new Error('Both rays must have the same length')
  }
  const f1_part = fractionalPart(f1)
  const f2_part = fractionalPart(f2)
  if (f1_part.isZero() || f2_part.isZero()) {
    throw new Error('Both f1 and f2 must have non-zero fractional parts')
  }

  const optimalMu = findOptimalMu(f1_part, f2_part, ray1, ray2)
  coreLog(`2D-Simplex: Found optimal μ = ${optimalMu.toString()} for f1=${f1_part.toString()}, f2=${f2_part.toString()}`)

  const coefficients = generate2DSimplexProper(f1_part, f2_part, ray1, ray2, optimalMu)

  if (coefficients.length > 0) {
    coreLog(`2D-Simplex coefficients: [${coefficients.map(c => c.toNumber().toFixed(4)).join(', ')}]`)
    const gmiCoeffs = generate2DSimplex(f1_part, f2_part, ray1, ray2, optimalMu)
    coreLog(`GMI combination coeffs:  [${gmiCoeffs.map(c => c.toNumber().toFixed(4)).join(', ')}]`)
    const isDifferent = coefficients.some((coeff, i) => 
      Math.abs(coeff.toNumber() - gmiCoeffs[i].toNumber()) > 1e-6
    )
    coreLog(`2D-Simplex vs GMI combination: ${isDifferent ? 'DIFFERENT' : 'SAME'} ✨`)
  }

  if (showBestMu) {
    return { coefficients, optimalMu }
  } else {
    return coefficients
  }
}

function findOptimalMu(f1: Fraction, f2: Fraction, ray1: Fraction[], ray2: Fraction[]): Fraction {
  const f = [f1, f2]
  const allRValues: Fraction[][] = []
  for (let j = 0; j < ray1.length; j++) {
    const r1_frac = fractionalPart(ray1[j])
    const r2_frac = fractionalPart(ray2[j])
    const r1_corr = r1_frac.greaterThanOrEqual(f1) ? r1_frac.subtract(Fraction.one()) : r1_frac
    const r2_corr = r2_frac.greaterThanOrEqual(f2) ? r2_frac.subtract(Fraction.one()) : r2_frac
    allRValues.push([r1_corr, r2_corr])
  }
  return findOptimalMuInternal(f, allRValues)
}

function findOptimalMuInternal(f: Fraction[], rValues: Fraction[][]): Fraction {
  const MU_EPS = Fraction.fromDecimal(1e-6)
  const clampMu = (mu: Fraction) => {
    if (mu.lessThanOrEqual(MU_EPS)) return MU_EPS
    const oneMinus = Fraction.one().subtract(MU_EPS)
    if (mu.greaterThanOrEqual(oneMinus)) return oneMinus
    return mu
  }

  if (rValues.length === 0) {
    return MU_EPS
  }

  const n = rValues.length
  const [f1, f2] = f
  const c = f1.subtract(f2)
  const d = f2

  interface PrecomputedData {
    coeffs: {
      g1: [Fraction, Fraction]
      g2: [Fraction, Fraction]
      g3: [Fraction, Fraction]
      g4: [Fraction, Fraction]
    }
    g2Val: Fraction
    g4Val: Fraction
  }

  const precomputed: PrecomputedData[] = []
  const kinkCandidates: Array<[Fraction, number]> = []

  for (let j = 0; j < n; j++) {
    const [r1, r2] = rValues[j]
    const c1 = r1.divide(f1.subtract(Fraction.one()))
    const c2 = r2.divide(f2.subtract(Fraction.one()))
    const iStar = c1.greaterThanOrEqual(c2) ? 1 : 2
    const g2Val = iStar === 1 ? c1 : c2
    const g4Val = iStar === 1 ? c2 : c1

    const coeffs = {
      g1: [r1.subtract(r2), r2] as [Fraction, Fraction],
      g2: [g2Val.multiply(c), g2Val.multiply(d)] as [Fraction, Fraction],
      g3: (iStar === 1
        ? [r1.subtract(r2).add(Fraction.one()), r2]
        : [r1.subtract(r2).subtract(Fraction.one()), r2.add(Fraction.one())]) as [Fraction, Fraction],
      g4: [g4Val.multiply(c), g4Val.multiply(d)] as [Fraction, Fraction]
    }

    precomputed.push({ coeffs, g2Val, g4Val })

    for (const gCoeffs of [coeffs.g1, coeffs.g3]) {
      for (const K of [g2Val, g4Val]) {
        const den = gCoeffs[0].subtract(K.multiply(c))
        if (!den.isZero()) {
          const muSol = K.multiply(d).subtract(gCoeffs[1]).divide(den)
          if (muSol.greaterThan(Fraction.zero()) && muSol.lessThan(Fraction.one())) {
            kinkCandidates.push([muSol, j])
          }
        }
      }
    }
  }

  const eventPoints = new Map<string, Fraction>()
  eventPoints.set(Fraction.zero().toString(), Fraction.zero())
  eventPoints.set(Fraction.one().toString(), Fraction.one())
  for (const [mu] of kinkCandidates) {
    eventPoints.set(mu.toString(), mu)
  }

  const sortedPoints = Array.from(eventPoints.values()).sort((a, b) => a.subtract(b).toNumber())

  const kinksByMu = new Map<string, number[]>()
  for (const [mu, j] of kinkCandidates) {
    const key = mu.toString()
    if (!kinksByMu.has(key)) kinksByMu.set(key, [])
    kinksByMu.get(key)!.push(j)
  }

  const getCoeffsForInterval = (muTest: Fraction, j: number): [Fraction, Fraction] => {
    const pData = precomputed[j]
    const den = c.multiply(muTest).add(d)
    if (den.isZero()) {
      return pData.coeffs.g2
    }
    const g1Val = pData.coeffs.g1[0].multiply(muTest).add(pData.coeffs.g1[1]).divide(den)
    const g3Val = pData.coeffs.g3[0].multiply(muTest).add(pData.coeffs.g3[1]).divide(den)
    const term1 = g3Val.greaterThan(pData.g4Val) ? g3Val : pData.g4Val
    const term2 = g1Val.greaterThan(pData.g2Val) ? g1Val : pData.g2Val
    if (term1.lessThan(term2)) {
      return g3Val.greaterThan(pData.g4Val) ? pData.coeffs.g3 : pData.coeffs.g4
    } else {
      return g1Val.greaterThan(pData.g2Val) ? pData.coeffs.g1 : pData.coeffs.g2
    }
  }

  if (sortedPoints.length <= 1) {
    return MU_EPS
  }

  const muTest = sortedPoints[0].add(sortedPoints[1]).divide(new Fraction(2))
  const currentCoeffs: Array<[Fraction, Fraction]> = []
  for (let j = 0; j < n; j++) {
    currentCoeffs.push(getCoeffsForInterval(muTest, j))
  }

  let alphaSum = currentCoeffs.reduce((sum, [a, _]) => sum.add(a.multiply(a)), Fraction.zero())
  let betaSum = currentCoeffs.reduce((sum, [a, b]) => sum.add(a.multiply(b).multiply(new Fraction(2))), Fraction.zero())
  let gammaSum = currentCoeffs.reduce((sum, [_, b]) => sum.add(b.multiply(b)), Fraction.zero())

  const evalL = (mu: Fraction, alpha: Fraction, beta: Fraction, gamma: Fraction): Fraction => {
    const denSq = c.multiply(mu).add(d).multiply(c.multiply(mu).add(d))
    if (denSq.isZero()) {
      return new Fraction(Number.MAX_SAFE_INTEGER)
    }
    return alpha.multiply(mu).multiply(mu).add(beta.multiply(mu)).add(gamma).divide(denSq)
  }

  let muOptimal = Fraction.zero()
  let LMin = evalL(Fraction.zero(), alphaSum, betaSum, gammaSum)

  for (let k = 0; k < sortedPoints.length - 1; k++) {
    const pk = sortedPoints[k]
    const pk1 = sortedPoints[k + 1]
    const E = alphaSum.multiply(d).multiply(new Fraction(2)).subtract(betaSum.multiply(c))
    if (!E.isZero()) {
      const muKStar = betaSum.multiply(d).subtract(gammaSum.multiply(c).multiply(new Fraction(2))).divide(E.negate())
      if (muKStar.greaterThan(pk) && muKStar.lessThan(pk1)) {
        const LCand = evalL(muKStar, alphaSum, betaSum, gammaSum)
        if (LCand.lessThan(LMin)) {
          LMin = LCand
          muOptimal = muKStar
        }
      }
    }
    const LCandEndpoint = evalL(pk1, alphaSum, betaSum, gammaSum)
    if (LCandEndpoint.lessThan(LMin)) {
      LMin = LCandEndpoint
      muOptimal = pk1
    }
    if (k < sortedPoints.length - 2) {
      const muNextTest = pk1.add(sortedPoints[k + 2]).divide(new Fraction(2))
      const kinksAtPk1 = kinksByMu.get(pk1.toString()) || []
      for (const j of kinksAtPk1) {
        const [aOld, bOld] = currentCoeffs[j]
        const [aNew, bNew] = getCoeffsForInterval(muNextTest, j)
        alphaSum = alphaSum.add(aNew.multiply(aNew)).subtract(aOld.multiply(aOld))
        betaSum = betaSum.add(aNew.multiply(bNew).multiply(new Fraction(2))).subtract(aOld.multiply(bOld).multiply(new Fraction(2)))
        gammaSum = gammaSum.add(bNew.multiply(bNew)).subtract(bOld.multiply(bOld))
        currentCoeffs[j] = [aNew, bNew]
      }
    }
  }

  return clampMu(muOptimal)
}

function generate2DSimplexProper(
  f1: Fraction,
  f2: Fraction,
  ray1: Fraction[],
  ray2: Fraction[],
  mu: Fraction
): Fraction[] {
  const coefficients: Fraction[] = []
  for (let j = 0; j < ray1.length; j++) {
    const r1 = ray1[j]
    const r2 = ray2[j]
    const r1_frac = fractionalPart(r1)
    const r2_frac = fractionalPart(r2)
    const r1_corr = r1_frac.greaterThanOrEqual(f1) ? r1_frac.subtract(Fraction.one()) : r1_frac
    const r2_corr = r2_frac.greaterThanOrEqual(f2) ? r2_frac.subtract(Fraction.one()) : r2_frac
    const pi_val = calculate2DSimplexPiValue(mu, [r1_corr, r2_corr], [f1, f2])
    coefficients.push(pi_val)
  }
  return coefficients
}

function calculate2DSimplexPiValue(mu: Fraction, r: Fraction[], f: Fraction[]): Fraction {
  const [f1, f2] = f
  const [r1, r2] = r
  const c1 = r1.divide(f1.subtract(Fraction.one()))
  const c2 = r2.divide(f2.subtract(Fraction.one()))
  const iStar = c1.greaterThanOrEqual(c2) ? 1 : 2
  const g2Val = iStar === 1 ? c1 : c2
  const g4Val = iStar === 1 ? c2 : c1
  const denominator = mu.multiply(f1).add(Fraction.one().subtract(mu).multiply(f2))
  if (denominator.isZero()) {
    return Fraction.zero()
  }
  const g1Val = mu.multiply(r1).add(Fraction.one().subtract(mu).multiply(r2)).divide(denominator)
  const muIStar = iStar === 1 ? mu : Fraction.one().subtract(mu)
  const g3Val = g1Val.multiply(denominator).add(muIStar).divide(denominator)
  const term1 = g3Val.greaterThan(g4Val) ? g3Val : g4Val
  const term2 = g1Val.greaterThan(g2Val) ? g1Val : g2Val
  return term1.lessThan(term2) ? term1 : term2
}

// Alias kept for debug parity with legacy logs
function generate2DSimplex(
  f1: Fraction,
  f2: Fraction,
  ray1: Fraction[],
  ray2: Fraction[],
  mu: Fraction
): Fraction[] {
  return generate2DSimplexProper(f1, f2, ray1, ray2, mu)
}

/**
 * Compute efficacy of a cut (sum of coefficients)
 * Higher efficacy generally means stronger cut
 */
/* 
function computeEfficacy(coefficients: Fraction[]): Fraction {
  return coefficients.reduce((sum, coeff) => sum.add(coeff), Fraction.zero())
}
*/

/**
 * Validate that a cutting plane is valid
 * @param coefficients - Cut coefficients
 * @param rhs - Right-hand side of cut
 * @returns true if cut is valid
 */
export function validateCut(coefficients: Fraction[], rhs: Fraction): boolean {
  // A valid cut should have:
  // 1. At least one non-zero coefficient
  // 2. RHS should be positive (for standard form)
  
  const hasNonZero = coefficients.some(coeff => !coeff.isZero())
  const validRHS = rhs.isPositive()
  
  return hasNonZero && validRHS
}

/**
 * Compute the violation of a cut for a given point
 * @param coefficients - Cut coefficients  
 * @param rhs - Right-hand side of cut
 * @param point - Point to check
 * @returns violation amount (positive means violated)
 */
export function computeViolation(coefficients: Fraction[], rhs: Fraction, point: Fraction[]): Fraction {
  if (coefficients.length !== point.length) {
    throw new Error('Coefficients and point must have same length')
  }
  
  let lhs = Fraction.zero()
  for (let i = 0; i < coefficients.length; i++) {
    lhs = lhs.add(coefficients[i].multiply(point[i]))
  }
  
  return lhs.subtract(rhs)
}

/**
 * Available cutting plane generation functions
 */
export const CGF_TYPES = {
  GMI: 'gmi',
  GOMORY: 'gomory',
  TWO_D_SIMPLEX: '2dsimplex'
} as const

export type CGFType = typeof CGF_TYPES[keyof typeof CGF_TYPES]

/**
 * Generate a cut using the specified CGF type
 */
export function generateCut(
  cgfType: CGFType,
  f: Fraction | Fraction[],
  rays: Fraction[][],
  options?: { showBestMu?: boolean; mu?: Fraction }
): Fraction[] | { coefficients: Fraction[], optimalMu: Fraction } {
  switch (cgfType) {
    case CGF_TYPES.GMI:
      if (Array.isArray(f)) {
        throw new Error('GMI requires single f value')
      }
      return pi_gmi(f, rays)
      
    case CGF_TYPES.GOMORY:
      if (Array.isArray(f)) {
        throw new Error('Gomory requires single f value')
      }
      return pi_gomory(f, rays)
      
    case CGF_TYPES.TWO_D_SIMPLEX:
      if (!Array.isArray(f)) {
        throw new Error('2D-Simplex requires array of f values')
      }
      // If user provides μ, use it directly; otherwise find optimal μ
      if (options && (options as any).mu) {
        return pi_2dsimplex_with_mu(f, rays, (options as any).mu as Fraction)
      } else {
        return pi_2dsimplex(f, rays, options?.showBestMu || false)
      }
      
    default:
      throw new Error(`Unknown CGF type: ${cgfType}`)
  }
}

export function pi_2dsimplex_with_mu(
  f: Fraction[],
  rays: Fraction[][],
  mu: Fraction
): Fraction[] {
  if (rays.length !== 2) {
    throw new Error('pi_2dsimplex_with_mu requires exactly 2 rows')
  }
  if (f.length !== 2) {
    throw new Error('f must have exactly 2 elements for 2D-Simplex')
  }
  const [f1_raw, f2_raw] = f
  const [ray1, ray2] = rays
  if (ray1.length !== ray2.length) {
    throw new Error('Both rays must have the same length')
  }
  const f1 = fractionalPart(f1_raw)
  const f2 = fractionalPart(f2_raw)
  if (f1.isZero() || f2.isZero()) {
    throw new Error('Both f1 and f2 must have non-zero fractional parts')
  }
  if (mu.lessThanOrEqual(Fraction.zero()) || mu.greaterThanOrEqual(Fraction.one())) {
    throw new Error('μ must lie in the open interval (0,1)')
  }
  return generate2DSimplexProper(f1, f2, ray1, ray2, mu)
}
